import { makeAutoObservable } from 'mobx';
import { FormField } from '../../components/custom-dialog/CustomDialog';
import createStoreContext from '../createStoreContext';
import React from 'react';

export enum DialogFor {
  REPORT_GROUP = 'REPORT_GROUP',
  DELETE_REPORT_GROUP = 'DELETE_REPORT_GROUP',
  REPORT_MESSAGE = 'REPORT_MESSAGE',
  DELETE_REPORT_MESSAGE = 'DELETE_REPORT_MESSAGE',
  GENERAL_MEMORY = 'GENERAL_MEMORY',
  SAVED_PROMPT = 'SAVED_PROMPT',
  DELETE_GENERAL_MEMORY = 'DELETE_GENERAL_MEMORY',
  UPDATE_HANA_ACCESS = 'UPDATE_HANA_ACCESS',
  UPDATE_HANA_AUTO_INITIATE_ACCESS = 'UPDATE_HANA_AUTO_INITIATE_ACCESS',
  UPDATE_HANA_AUTO_MEMORY_INGESTION = 'UPDATE_HANA_AUTO_MEMORY_INGESTION',
  UPDATE_USER_ROLE = 'UPDATE_USER_ROLE',
  DELETE_ORGANIZATION = 'DELETE_ORGANIZATION',
  CONFIRM_DELETE_ORGANIZATION = 'CONFIRM_DELETE_ORGANIZATION',
  DELETE_USER = 'DELETE_USER',
  DELETE_SAVED_PROMPT = 'DELETE_SAVED_PROMPT',
  DELETE_SPACE = 'DELETE_SPACE',
  CONFIRM_DELETE_USER = 'CONFIRM_DELETE_USER',
  CANCEL_PAID_PLAN = 'CANCEL_PAID_PLAN',
  SUBMIT_DM_ANNOUNCEMENT = 'SUBMIT_DM_ANNOUNCEMENT',
  DELETE_DM_ANNOUNCEMENT = 'DELETE_DM_ANNOUNCEMENT',
  ABORT_DM_ANNOUNCEMENT = 'ABORT_DM_ANNOUNCEMENT',
  ABORT_MEMORY_BATCH = 'ABORT_MEMORY_BATCH',
  DELETE_MEMORY_BATCH = 'DELETE_MEMORY_BATCH',
  ADD_GOOGLE_DOC_MEMORY = 'ADD_GOOGLE_DOC_MEMORY',
  ADD_RECURSIVE_URL_MEMORY = 'ADD_RECURSIVE_URL_MEMORY',
  DELETE_RECURSIVE_URL_MEMORY = 'DELETE_RECURSIVE_URL_MEMORY',
  DELETE_PDF_MEMORY = 'DELETE_PDF_MEMORY',
  DELETE_DOC_DOCX_MEMORY = 'DELETE_DOC_DOCX_MEMORY',
  DELETE_AUDIO_MEMORY = 'DELETE_AUDIO_MEMORY',
  DELETE_IMAGE_MEMORY = 'DELETE_IMAGE_MEMORY',
  DELETE_GOOGLE_DRIVE_MEMORY = 'DELETE_GOOGLE_DRIVE_MEMORY',
  DELETE_GOOGLE_DOC_MEMORY = 'DELETE_GOOGLE_DOC_MEMORY',
  DELETE_GMAIL_MEMORY = 'DELETE_GMAIL_MEMORY',
  ENABLE_AUTO_RESYNC_MEMORY = 'ENABLE_AUTO_RESYNC_MEMORY',
  DISABLE_AUTO_RESYNC_MEMORY = 'DISABLE_AUTO_RESYNC_MEMORY',
  REDIRECT_PROXY = 'REDIRECT_PROXY',
  DELETE_REDIRECT_PROXY = 'DELETE_REDIRECT_PROXY',
  ADD_CONFLUENCE_MEMORY = 'ADD_CONFLUENCE_MEMORY',
  ADD_JIRA_MEMORY = 'ADD_JIRA_MEMORY',
  ADD_GOOGLE_CHAT_SPACE_MEMORY = 'ADD_GOOGLE_CHAT_SPACE_MEMORY',
  ADD_YOUTUBE_CAPTIONS_MEMORY = 'ADD_YOUTUBE_CAPTIONS_MEMORY',
  DELETE_CONFLUENCE_MEMORY = 'DELETE_CONFLUENCE_MEMORY',
  DELETE_JIRA_MEMORY = 'DELETE_JIRA_MEMORY',
  DELETE_GOOGLE_CHAT_SPACE_MEMORY = 'DELETE_GOOGLE_CHAT_SPACE_MEMORY',
  DELETE_YOUTUBE_CAPTIONS_MEMORY = 'DELETE_YOUTUBE_CAPTIONS_MEMORY',
  DELETE_ORGANIZATION_AUTO_INGESTED_MEMORY = 'DELETE_ORGANIZATION_AUTO_INGESTED_MEMORY',
  DELETE_USER_AUTO_INGESTED_MEMORY = 'DELETE_USER_AUTO_INGESTED_MEMORY',
  PROMOTE_ADMIN = 'PROMOTE_ADMIN',
  CONFIRM_PROMOTE_ADMIN = 'CONFIRM_PROMOTE_ADMIN',
  CHANGE_ACTIVE_ORG = 'CHANGE_ACTIVE_ORG',
  CONNECT_CONFLUENCE = 'CONNECT_CONFLUENCE',
  CONNECT_EXPOSE_API = 'CONNECT_EXPOSE_API',
  CONNECT_JIRA = 'CONNECT_JIRA',
  PROVIDE_GOOGLE_OAUTH_CONSENT = 'PROVIDE_GOOGLE_OAUTH_CONSENT',
  DISCONNECT_CONNECTOR = 'DISCONNECT_CONNECTOR',
  FILE_PICKER_DIALOG = 'FILE_PICKER_DIALOG',
  CONFIRM_PDF_INGESTION = 'CONFIRM_PDF_INGESTION',
  CONFIRM_DOC_DOCX_INGESTION = 'CONFIRM_DOC_DOCX_INGESTION',
  CONFIRM_AUDIO_INGESTION = 'CONFIRM_AUDIO_INGESTION',
  CONFIRM_IMAGE_INGESTION = 'CONFIRM_IMAGE_INGESTION',
  CONFIRM_GENERAL_MEMORY = 'CONFIRM_GENERAL_MEMORY',
  CONFIRM_GOOGLE_DOC_MEMORY = 'CONFIRM_GOOGLE_DOC_MEMORY',
  CONFIRM_GOOGLE_DRIVE_MEMORY = 'CONFIRM_GOOGLE_DRIVE_MEMORY',
  CONFIRM_GMAIL_MEMORY = 'CONFIRM_GMAIL_MEMORY',
  CONFIRM_RECURSIVE_URL_MEMORY = 'CONFIRM_RECURSIVE_URL_MEMORY',
  CONFIRM_CONFLUENCE_MEMORY = 'CONFIRM_CONFLUENCE_MEMORY',
  CONFIRM_JIRA_MEMORY = 'CONFIRM_JIRA_MEMORY',
  CONFIRM_GOOGLE_CHAT_SPACE_MEMORY = 'CONFIRM_GOOGLE_CHAT_SPACE_MEMORY',
  CONFIRM_YOUTUBE_CAPTIONS_MEMORY = 'CONFIRM_YOUTUBE_CAPTIONS_MEMORY',
  UPDATE_MEMORY_TITLE = 'UPDATE_MEMORY_TITLE',
  UPDATE_MEMORY_TAGS = 'UPDATE_MEMORY_TAGS',
  UPDATE_USE_CASE_INSTRUCTIONS = 'UPDATE_USE_CASE_INSTRUCTIONS',
  UPDATE_SHEET_OPTIONS = 'UPDATE_SHEET_OPTIONS',
  UPDATE_GMAIL_OPTIONS = 'UPDATE_GMAIL_OPTIONS',
}

export type DialogueState = 'OPEN' | 'CLOSED' | 'LOADING' | 'ACCEPT' | 'REJECT';

export interface SubmitDialogFor {
  dialogType: DialogFor | null;
}

export const [DialogStoreProvider, useDialogStore] = createStoreContext(
  class DialogStore {
    dialogType: DialogFor | null = null;
    dialogueState: DialogueState = 'CLOSED';
    dialogFormFields: FormField[] = [];
    dialogTitle = '';
    dialogContent: React.ReactNode = '';
    dialogStyle? = {};
    isSubmitDisabled = false;

    public constructor() {
      makeAutoObservable(this);
    }

    updateDialogueState = (state: DialogueState) => {
      this.dialogueState = state;
    };

    updateDialogMetaData = (
      title: string,
      content: React.ReactNode,
      type: DialogFor,
      formFields: FormField[],
      dialogStyle?: React.CSSProperties,
    ) => {
      this.dialogStyle = dialogStyle;
      this.dialogTitle = title;
      this.dialogContent = content;
      this.dialogType = type;
      this.dialogFormFields = formFields;
      this.isSubmitDisabled = false;
    };

    updateSubmitDisabled = (disabled: boolean) => {
      this.isSubmitDisabled = disabled;
    };

    resetDialog = () => {
      this.dialogueState = 'CLOSED';
      this.dialogStyle = {};
      this.dialogType = null;
      this.dialogFormFields = [];
      this.dialogTitle = '';
      this.dialogContent = '';
      this.isSubmitDisabled = false;
    };

    resetFormFieldsValue = (formFields: FormField[]) => {
      return formFields.map((formField) => {
        switch (formField.type) {
          case 'multi-select':
            formField.multiSelectValue = [];
            break;
          case 'tags':
            formField.value = [];
            break;

          default:
            formField.value = '';
        }

        return formField;
      });
    };
  },
);

export type DialogStore = ReturnType<typeof useDialogStore>;
